// src/pages/TourList.jsx
import React, { useState, useEffect, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { BentoCard } from "Components/uiElements/UIComponents/bento-grid/bento-grid";
import "./TourList.css";
import {
  GridContainer,
  scrollToTop,
  SkeletonTourListPreload,
} from "MainApp/assets/visual/styles/globalStyles/globalJsxStyles";
import { ToursContext } from "MainApp/assets/utils/context/TourContext";
import { AuthContext } from "MainApp/assets/utils/context/AuthContext";

/* ---------- helpers ---------- */
const pickI18n = (objOrStr, preferredLocales) => {
  if (!objOrStr) return "";
  if (typeof objOrStr === "string") return objOrStr;
  for (const lc of preferredLocales) {
    const v = objOrStr?.[lc];
    if (typeof v === "string" && v.trim()) return v;
  }
  const first = Object.values(objOrStr).find((v) => typeof v === "string" && v.trim());
  return first || "";
};
const withBase = (url) => {
  const u = url || "";
  if (!u) return "";
  if (u.startsWith("http://") || u.startsWith("https://")) return u;
  const base = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/+$/, "");
  const path = u.startsWith("/") ? u : `/${u}`;
  return `${base}${path}`;
};
const slugify = (s = "") =>
  String(s)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
const preferredLocalesFromI18n = (i18n) => {
  const out = [];
  const lang = i18n?.language || i18n?.resolvedLanguage;
  if (lang) out.push(lang.split("-")[0], lang);
  const fb = i18n?.options?.fallbackLng;
  if (Array.isArray(fb)) out.push(...fb);
  else if (typeof fb === "string") out.push(fb);
  else if (fb && typeof fb === "object") out.push(...Object.values(fb).flat());
  out.push("default");
  return [...new Set(out.filter(Boolean))];
};

function TourList() {
  const { i18n } = useTranslation(); // re-render al cambiar idioma
  const { tours, setTours, setLoading } = useContext(ToursContext);
  const { token, axiosInstance, isAuthReady } = useContext(AuthContext);
  const getListUrl = "/toursAPI/get-list";

  // 1) Fetch: guarda i18n completos y el slug canónico (NO resolver aquí)
  useEffect(() => {
    if (tours.length === 0 && isAuthReady && token && axiosInstance) {
      axiosInstance
        .get(getListUrl)
        .then((response) => {
          console.log("API response:", response.data);
          if (response.data && Array.isArray(response.data.tours)) {
            const transformed = response.data.tours.map((tour) => ({
              id: tour.tourUUID,
              // i18n completos
              titleI18n: tour.name || {},
              descriptionI18n: tour.description || {},
              slugI18n: tour.slug_i18n || {},
              // 👇 slug canónico que EXISTE en backend
              slug: tour.slug,
              images: withBase(tour.cover_image_uri),
              price: tour.priceMin,
              sprice: tour.stripe_price_id,
              duration: tour.duration,
              durations: Array.isArray(tour.durations) ? tour.durations : [],
              position: Number(tour.position ?? 0),
              personsInsteadTime: Boolean(tour.uses_people_instead_of_times),
              usesPeopleAndTimesPricing: Boolean(
                tour.uses_people_and_times_pricing
              ),
              uses_people_and_times_pricing: Boolean(
                tour.uses_people_and_times_pricing
              ),
              preferLowerPeoplePriceTier: Boolean(
                tour.prefer_lower_people_price_tier
              ),
              prefer_lower_people_price_tier: Boolean(
                tour.prefer_lower_people_price_tier
              ),
              frontendUsesPeoplePriceTiers: Boolean(
                tour.frontend_uses_people_price_tiers
              ),
              frontend_uses_people_price_tiers: Boolean(
                tour.frontend_uses_people_price_tiers
              ),
              vehiclesSmall: Boolean(tour.show_vehicles_as_lowest_capacity),
            }));
            transformed.sort((a, b) => a.position - b.position);
            setTours(transformed);
          } else {
            console.error("API response does not contain an array of tours:", response.data);
          }
        })
        .catch((error) => {
          console.error("There was an error fetching the tours!", error);
        })
        .finally(() => setLoading(false));
    }
  }, [tours, isAuthReady, token, axiosInstance, getListUrl, setTours, setLoading]);

  // 2) Deriva en cada render según idioma (solo título/descripcion)
  const preferred = useMemo(() => preferredLocalesFromI18n(i18n), [i18n.language]);

  const computedTours = useMemo(() => {
    return (tours || []).map((tour) => {
      const title = pickI18n(tour.titleI18n ?? tour.title, preferred);
      const description = pickI18n(tour.descriptionI18n ?? tour.description, preferred);
      // ⚠️ El ENLACE usa SIEMPRE tour.slug (canónico). Si por algún motivo faltara, mete fallback:
      const linkSlug = tour.slug || slugify(title) || "";
      return { ...tour, title, description, slug: linkSlug };
    });
  }, [tours, preferred]);

  const [imageLoaded, setImageLoaded] = useState(false);
  const handleImageLoad = () => setImageLoaded(true);

  scrollToTop();

  return (
    <div className="bodySelectTourList">
      {!imageLoaded && <SkeletonTourListPreload />}
      <GridContainer>
        {Array.isArray(computedTours) &&
          computedTours.length > 0 &&
          computedTours.map((tour, index) => (
            <BentoCard
              key={tour.id}
              tourState={tour}
              index={index}
              {...tour}
              onImagesLoad={handleImageLoad}
              imageLoaded={imageLoaded}
            />
          ))}
      </GridContainer>
    </div>
  );
}

export default TourList;
